// src/services/apiTracker.js
// *******************************************************************************
// API TRACKER SERVICE
// *******************************************************************************
// Sistema profissional de tracking e debugging de APIs
// Permite abortar, logar e monitorar chamadas de API antes de enviá-las
// *******************************************************************************

import { onProduction } from 'utils/helpers';
import apiConfig from 'configs/fetchInterceptor';
import { getParams, getQuery, matchUrlSchema } from 'utils/urlUtils';
import consoleX from 'utils/consoleX';

// *******************************************************************************
// INTERNAL STATE
// *******************************************************************************
const trackers = new Map(); // Map<url, config>

// *******************************************************************************
// TRACKER REGISTRY
// *******************************************************************************
/**
 * Registra um tracker para uma URL específica
 *
 * @param {string} url - URL da API para rastrear
 * @param {object} config - Configuração do tracker
 * @param {boolean} [config.enable=false] - Se false, tracker é ignorado (para desabilitar sem remover código)
 * @param {string} [config.environment='development'] - Ambiente para tracking:
 *   - 'development': ativa APENAS em desenvolvimento
 *   - 'production': ativa em QUALQUER ambiente (dev + prod)
 * @param {boolean} [config.abort=false] - Se deve abortar a chamada (false = apenas loga)
 * @param {string[]} [config.methods] - Métodos HTTP para rastrear (default: ['post', 'put', 'del'])
 *
 * @example
 * trackApi(API.AUTH.SIGNUP, {
 *   enable: true,
 *   environment: 'development',
 *   abort: true,
 *   methods: ['post']
 * });
 */
const trackApi = async (
  url,
  {
    enable = false,
    environment = 'development', // 'development' | 'production'
    abort = false,
    methods = ['post', 'put', 'del'],
  } = {}
) => {
  if (!url) {
    consoleX({
      message: 'URL is required for trackApi',
      emoji: '🚫',
      flag: 'e',
      title: 'API TRACKER',
    });
    return;
  }

  // Se enable = false, ignora completamente
  if (!enable) {
    consoleX({
      message: 'Tracker disabled',
      emoji: '⏭️',
      flag: 'd',
      title: 'API TRACKER',
      info: { URL: url },
      color: '#757575',
    });
    return;
  }

  const normalizedUrl = url.split('?')[0];
  const isProd = onProduction();

  // Lógica de tracking:
  // - 'development': só ativa em desenvolvimento
  // - 'production': ativa em QUALQUER ambiente (dev + prod)
  const shouldTrack = environment === 'production' || (environment === 'development' && !isProd);

  if (!shouldTrack) {
    consoleX({
      message: 'Tracker ignored',
      emoji: '⏭️',
      flag: 'd',
      title: 'API TRACKER',
      info: {
        URL: normalizedUrl,
        'Ambiente configurado': environment,
        'Ambiente atual': isProd ? 'production' : 'development',
      },
    });
    return;
  }

  const config = {
    environment,
    abort,
    methods: Array.isArray(methods) ? methods.map(m => m.toLowerCase()) : [],
    originalUrl: url,
  };

  trackers.set(normalizedUrl, config);
  consoleX({
    message: `Tracker registered`,
    flag: environment === 'production' || abort ? 'e' : 'w',
    emoji: abort ? '🛑' : '📣',
    title: 'API TRACKER',
    info: {
      URL: normalizedUrl,
      Environment: environment,
      Abort: abort,
      Methods: methods.join(', ').toUpperCase() || 'ALL',
    },
  });

  await showTrackerNotification(normalizedUrl, config);
};

/**
 * Shows visual notification when URL is invalid
 */
const showInvalidUrlNotification = async (url, method) => {
  try {
    const appService = (await import('./appService.js')).default;

    const tryShowNotification = (attempt = 0) => {
      if (appService.appInstance?.notification) {
        appService.notification({
          key: `invalidUrl-${Date.now()}`,
          flag: 'error',
          title: '🚨 Invalid or Undefined URL',
          // description: `Method: ${method?.toUpperCase() || 'UNKNOWN'} | URL: ${url || 'undefined'} | Check console for details`,
          description: url,
          duration: 0, // Don't close automatically
          noTranslateDescription: true,
        });
      } else if (attempt < 5) {
        setTimeout(() => tryShowNotification(attempt + 1), 200);
      }
    };

    setTimeout(() => tryShowNotification(), 100);
  } catch (error) {
    // Ignore notification error silently
  }
};

/**
 * Mostra notification visual ao registrar um tracker
 * - Desenvolvimento: sempre mostra notification
 * - Produção: só mostra se environment for 'production'
 */
const showTrackerNotification = async (url, config) => {
  try {
    const appService = (await import('./appService.js')).default;
    const { environment, abort, methods } = config;
    // const isProd = onProduction();

    if (environment === 'development') return;

    // Em produção, só mostra notification se for 'production'
    // if (isProd && environment === 'development') {
    //   return;
    // }

    // Determina flag e título baseado no environment e abort
    const flag = environment === 'production' ? 'error' : 'warning';
    // [API TRACKER] POST /v1/auth/signup | 🛑 ABORTED!

    const title = `[API TRACKER] ${abort ? '🛑 ABORT' : '📣 REPORT'}`;
    // const methodsText = methods.length > 0 ? `[${methods.join(', ').toUpperCase()}]` : '';

    // Função para tentar mostrar a notification quando appInstance estiver pronto

    const tryShowNotification = (attempt = 0) => {
      if (appService.appInstance?.notification) {
        appService.notification({
          key: `apiTracker-${url}-${Date.now()}`,
          flag,
          title,
          description: url,
          duration: 0,
          noTranslateDescription: true, // Don't translate URL
        });
      } else if (attempt < 10) {
        setTimeout(() => tryShowNotification(attempt + 1), 500);
      } else {
        console.warn('[apiTracker] appInstance não inicializado após 5s');
      }
    };

    setTimeout(() => tryShowNotification(), 500);
  } catch (error) {
    console.warn('[apiTracker] Não foi possível mostrar notification:', error);
  }
};

/**
 * Remove um tracker
 */
const untrackApi = async url => {
  const normalizedUrl = url.split('?')[0];
  const wasTracked = trackers.has(normalizedUrl);
  trackers.delete(normalizedUrl);

  if (wasTracked) {
    consoleX({
      message: 'Tracker removed',
      emoji: '❌',
      flag: 'd',
      title: 'API TRACKER',
      info: {
        URL: normalizedUrl,
      },
    });

    // Fecha notification associada (tenta remover possíveis keys)
    try {
      const appService = (await import('./appService.js')).default;
      if (typeof appService?.notification?.close === 'function') {
        appService.notification.close(`apiTracker-${normalizedUrl}`);
      }
    } catch (error) {
      // Ignora erro silenciosamente
    }
  }
};

/**
 * Limpa todos os trackers
 */
const clearAllTrackers = () => {
  const count = trackers.size;
  trackers.clear();
  consoleX({
    message: 'All tracker removed',
    emoji: '🧹',
    flag: 'd',
    title: 'API TRACKER',
    info: {
      'Total removido': count,
    },
  });
};

/**
 * Lista todos os trackers ativos
 */
const listTrackers = () => {
  const trackersArray = Array.from(trackers.entries());
  const trackersInfo = {};

  trackersArray.forEach(([url, config], index) => {
    trackersInfo[`#${index + 1} - ${url}`] = {
      environment: config.environment,
      abort: config.abort,
      methods: config.methods.join(', ').toUpperCase() || 'ALL',
    };
  });

  consoleX({
    message: `${trackersArray.length} active trackers found:`,
    emoji: '📋',
    flag: 'd',
    title: 'API TRACKER',
    info: trackersArray.length > 0 ? trackersInfo : { Status: 'Nenhum tracker ativo' },
  });

  return trackersArray;
};

// *******************************************************************************
// TRACKER INTERCEPTOR
// *******************************************************************************
/**
 * Intercepta e valida uma chamada de API antes de executá-la
 *
 * @param {string} url - URL da API
 * @param {string} method - Método HTTP (get, post, put, delete)
 * @param {object} data - Dados da requisição (already decorated if decorator was executed)
 * @param {object} headers - Headers da requisição
 * @param {object} decoratorMeta - Metadata from decorator execution (optional)
 * @param {object} decoratorMeta.dataRaw - Raw data before decorator
 * @param {object} decoratorMeta.dataDecorated - Decorated data after decorator
 * @returns {boolean} - true se deve continuar, false se deve abortar
 */
const interceptApiCall = (url, method, data = {}, headers = {}, decoratorMeta = null) => {
  // ⚠️ Critical validation: URL must exist and be a string
  if (!url || typeof url !== 'string') {
    // 🚨 CRITICAL ERROR: Show VERY visible error in console
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.error(
      '%c🚨 CRITICAL ERROR: INVALID OR UNDEFINED URL',
      'color: #ff0000; font-weight: bold; font-size: 16px; background: #ffebee; padding: 8px;'
    );
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.error('%cHTTP Method:', 'font-weight: bold;', method?.toUpperCase() || 'UNKNOWN');
    console.error('%cReceived URL:', 'font-weight: bold;', url);
    console.error('%cURL Type:', 'font-weight: bold;', typeof url);
    console.error('%cPayload/Data:', 'font-weight: bold;', data);
    console.error('%cHeaders:', 'font-weight: bold;', headers);
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.error('%cStack Trace (where error occurred):', 'font-weight: bold; color: #ff6b6b;');
    console.trace();
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    // Try to show visual notification
    showInvalidUrlNotification(url, method).then();

    // Return false to abort the request
    return false;
  }

  const normalizedUrl = url.split('?')[0];
  let tracker = null;
  let matchedUrl = null;

  // Tenta match exato primeiro
  if (trackers.has(normalizedUrl)) {
    tracker = trackers.get(normalizedUrl);
    matchedUrl = normalizedUrl;
  } else {
    // Tenta match usando schema (para URLs com parâmetros dinâmicos como :cep, :_id, etc)
    for (const [trackedUrl, config] of trackers.entries()) {
      // Usa matchUrlSchema para comparar URL real com schema
      if (matchUrlSchema(normalizedUrl, trackedUrl)) {
        tracker = config;
        matchedUrl = trackedUrl;
        break;
      }
    }
  }

  if (!tracker) return true;

  const methodLower = method.toLowerCase();
  if (tracker.methods.length > 0 && !tracker.methods.includes(methodLower)) {
    return true;
  }

  // LOGGING - Always cyan, collapsed group
  const groupTitle = `[API TRACKER] ${method.toUpperCase()} ${url} | ${tracker.abort ? '🛑 INTENTIONALLY ABORTED (NOT A BUG)' : '📋 REPORTED (Request Allowed)'}`;

  console.groupCollapsed('%c' + groupTitle, 'color: #00bcd4; font-weight: bold;');
  console.log('🌍 Environment:', tracker.environment);
  console.log('🔧 Methods:', tracker.methods.join(', ').toUpperCase() || 'ALL');

  if (tracker.abort) {
    console.log('📍 Endpoint:', matchedUrl);
    console.log('🔗 Params:', getParams(url, matchedUrl));
    console.log('🔍 Query:', getQuery(url));
    console.log('📦 Headers (default):', apiConfig?.defaults?.headers);
    console.log('📦 Headers (append):', headers);

    // Show RAW and DECORATED data if decorator was executed
    if (decoratorMeta?.dataRaw && decoratorMeta?.dataDecorated) {
      console.log(
        '%c📦 Request RAW Data (Object):',
        'color: #ffa94d; font-weight: bold;',
        decoratorMeta.dataRaw
      );
      console.groupCollapsed('%c📋 RAW (JSON - Copy):', 'color: #ffa94d; font-weight: bold;');
      console.log(JSON.stringify(decoratorMeta.dataRaw, null, 2));
      console.groupEnd();

      console.log(
        '%c🎨 Request DECORATED Data (Object):',
        'color: #748ffc; font-weight: bold;',
        decoratorMeta.dataDecorated
      );
      console.groupCollapsed('%c📋 DECORATED (JSON - Copy):', 'color: #748ffc; font-weight: bold;');
      console.log(JSON.stringify(decoratorMeta.dataDecorated, null, 2));
      console.groupEnd();
    } else if (data && typeof data === 'object') {
      // Show regular Body if no decorator was executed
      console.log('📦 Body (Object):', data);

      try {
        console.log('%c📋 Body (JSON - Copy to Postman):', 'color: #51cf66; font-weight: bold;');
        console.log(JSON.stringify(data, null, 2));
        console.log('%c📋 Body (One-line):', 'color: #51cf66;');
        console.log(JSON.stringify(data));
      } catch (e) {
        console.warn('⚠️ Não foi possível stringificar o body:', e);
      }
    }

    // Stack trace para rastrear origem da chamada
    console.groupCollapsed('%c🎯 Stack', 'font-weight: normal');
    console.trace();
    console.groupEnd();
  }

  console.groupEnd();

  // ABORT: Throw exception to immediately stop execution
  if (tracker.abort) {
    // Show prominent warning before throwing error
    console.warn(
      '%c🛑 API TRACKER: INTENTIONALLY ABORTING REQUEST (THIS IS NOT A BUG!)',
      'background: #ff6b6b; color: white; font-weight: bold; font-size: 14px; padding: 8px; border-radius: 4px;'
    );
    // console.warn(`Request blocked: ${method.toUpperCase()} ${url}`);
    // console.warn('To allow this request: Set abort:false in src/configs/TrackingApis.js');
    // console.warn('─'.repeat(80));

    const error = new Error(
      `🛑 [API TRACKER] CODE INTENTIONALLY ABORTED BY API-TRACKING - NOT A BUG!\n` +
        `Request blocked: ${method.toUpperCase()} ${url}\n` +
        `This is expected behavior when abort:true in TrackingApis.js\n` +
        `Check console logs above for request details (headers, body, stack trace)\n` +
        `To allow request: Set abort:false in src/configs/TrackingApis.js`
    );
    error.isApiTrackerAbort = true; // Flag to identify this error
    error.trackerConfig = tracker;
    error.requestDetails = { url, method, data, headers };
    throw error;
  }

  return true;
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export { trackApi, untrackApi, clearAllTrackers, listTrackers, interceptApiCall };
